import { useRef, useState, type DragEvent, type KeyboardEvent } from 'react';

type Props = {
  accept?: string;
  disabled?: boolean;
  onFile: (file: File) => void;
  label?: string;
  hint?: string;
};

// Single-file dropzone that supports both click-to-browse and drag/drop.
// Keyboard activation (Enter / Space) opens the native file picker.
export function Dropzone({
  accept = '.csv,text/csv',
  disabled = false,
  onFile,
  label = 'Drop your delivery CSV here, or click to browse.',
  hint = 'Max 5 MB. UTF-8 CSV only.',
}: Props): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [hovering, setHovering] = useState(false);

  const openPicker = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openPicker();
    }
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setHovering(false);
    if (disabled) return;
    const file = e.dataTransfer.files?.[0];
    if (file) onFile(file);
  };

  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!disabled) setHovering(true);
  };

  const onDragLeave = () => setHovering(false);

  const base =
    'flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed ' +
    'px-6 py-12 text-center transition-colors cursor-pointer ' +
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2';
  const state = disabled
    ? 'border-border bg-bg-soft text-ink-3 cursor-not-allowed'
    : hovering
      ? 'border-primary bg-bg-soft text-ink'
      : 'border-border bg-bg text-ink-2 hover:border-primary hover:text-ink';

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled || undefined}
      aria-label={label}
      className={`${base} ${state}`}
      onClick={openPicker}
      onKeyDown={onKey}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
    >
      <p className="text-sm font-medium">{label}</p>
      <p className="text-xs">{hint}</p>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        data-testid="dropzone-input"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onFile(file);
          // Reset so picking the same file twice re-fires onChange.
          e.target.value = '';
        }}
      />
    </div>
  );
}
